import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	type Operator,
	type TerminalClause,
	isOperator,
	creators,
	OPERATOR_EQUALS,
} from '@atlaskit/jql-ast';
import { useIntl } from '@atlassian/jira-intl';
import type {
	AllowedOperators,
	OperatorOption,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { isListOperand } from '../../../../../utils/is-list-operand/index.tsx';
import { createOperatorOption } from '../../../../../utils/operators/create-operator-option.tsx';
import { resolveOperator } from '../../../../../utils/operators/resolve-operator.tsx';

const DEFAULT_OPERATOR = creators.operator(OPERATOR_EQUALS);

export const useSelectedOperator = ({
	fieldDisplayName,
	terminalClause,
	allowedOperators = [],
}: {
	fieldDisplayName: string;
	terminalClause: TerminalClause | undefined;
	allowedOperators?: Operator[];
}) => {
	const { formatMessage } = useIntl();

	/**
	 * List of operators allowed for the field
	 * We need to keep the object references to allow react select to know which is selected for screen readers
	 * It uses the reference for this, not the value (https://github.com/JedWatson/react-select/blob/master/packages/react-select/src/components/LiveRegion.tsx#L118)
	 */
	const allowedOperatorsOptions = useMemo<{
		singleOperators: OperatorOption[];
		listOperators: OperatorOption[];
	}>(
		() => ({
			singleOperators: [
				...new Set(
					allowedOperators.map((operator) =>
						resolveOperator({
							operatorValue: operator.value,
							isList: false,
						}),
					),
				),
			].map((operatorValue) =>
				createOperatorOption({
					fieldDisplayName,
					operator: creators.operator(operatorValue),
					formatMessage,
				}),
			),
			listOperators: [
				...new Set(
					allowedOperators.map((o) =>
						resolveOperator({
							operatorValue: o.value,
							isList: true,
						}),
					),
				),
			].map((operatorValue) =>
				createOperatorOption({
					fieldDisplayName,
					operator: creators.operator(operatorValue),
					formatMessage,
				}),
			),
		}),
		// Operator don't change after bootup

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	// Select if we need to show list or single operators
	const allowedOperatorsOptionsFiltered = useMemo<AllowedOperators>(
		() =>
			allowedOperatorsOptions[
				terminalClause?.operand && isListOperand(terminalClause.operand)
					? 'listOperators'
					: 'singleOperators'
			],
		[allowedOperatorsOptions, terminalClause?.operand],
	);

	// Selected operator for the field
	const [selectedOperator, setSelectedOperator] = useState<OperatorOption>(() => {
		let resolvedOperator = allowedOperatorsOptionsFiltered.find(
			(operator) =>
				terminalClause?.operator?.value && operator.value === terminalClause.operator.value,
		);

		if (!resolvedOperator) {
			resolvedOperator = allowedOperatorsOptionsFiltered.find(
				(operator) => operator.value === DEFAULT_OPERATOR.value,
			);
		}

		return resolvedOperator !== undefined
			? resolvedOperator
			: createOperatorOption({ fieldDisplayName, operator: DEFAULT_OPERATOR, formatMessage });
	});

	const setDefaultOperator = useCallback((): void => {
		setSelectedOperator(() => {
			const resolvedOperator = allowedOperatorsOptionsFiltered.find(
				(operator) => operator.value === DEFAULT_OPERATOR.value,
			);

			return resolvedOperator !== undefined
				? resolvedOperator
				: createOperatorOption({
						fieldDisplayName,
						operator: DEFAULT_OPERATOR,
						formatMessage,
					});
		});
	}, [allowedOperatorsOptionsFiltered, fieldDisplayName, formatMessage]);

	useEffect(() => {
		if (
			terminalClause &&
			terminalClause.operator?.value &&
			isOperator(terminalClause.operator.value) &&
			terminalClause.operator.value !== selectedOperator?.value
		) {
			const terminalOperator = terminalClause.operator;
			const resolvedOperator = allowedOperatorsOptionsFiltered.find(
				(operator) => operator.value === terminalOperator.value,
			);

			setSelectedOperator(
				resolvedOperator !== undefined
					? resolvedOperator
					: createOperatorOption({
							fieldDisplayName,
							operator: terminalOperator,
							formatMessage,
						}),
			);
		}
		// Only update when change come from outside of the hook (terminalClause)
		// We already manage selectedOperator changes internally

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formatMessage, terminalClause]);

	return {
		allowedOperators: allowedOperatorsOptionsFiltered,
		selectedOperator,
		setSelectedOperator,
		setDefaultOperator,
	};
};
